import * as React from 'react';

import { useComponentsConfig } from 'bundles/page-config-common/contexts/ComponentsConfigContext';
import EmbeddedPromoTemplatePageProviderWrapper from 'bundles/page-config-common/providers/enterprise/EmbeddedPromoTemplatePageProviderWrapper';
import type {
  PageConfig_GroupedComponentsWrapperMetadataFragment as GroupedComponentsWrapper,
  PageConfig_ComponentConfigFragment as PageConfigComponent,
} from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { NativeDataProvider } from 'bundles/promo-template/components/NativeDataContext';
import type { DOMAINS } from 'bundles/promo-template/constants/navigation';
import { PromoTemplateProvider } from 'bundles/promo-template/contexts/PromoTemplateContext';
import { PromoTemplateDomainProvider } from 'bundles/promo-template/contexts/PromoTemplateDomainContext';

const extractDomainAndSlug = (
  componentsConfig?: Array<PageConfigComponent | undefined | null> | null
): { domain: string; slug: string } => {
  let domain = '';
  let slug = '';

  if (componentsConfig) {
    for (const component of componentsConfig) {
      if (component?.__typename === 'PageConfig_EmbeddedPromoTemplatePage') {
        const parts = component.id.split(':');
        if (parts.length >= 2) {
          domain = parts[0];
          slug = parts[1];
        }
      } else if (component?.__typename === 'PageConfig_GroupedComponentsWrapper') {
        const result = extractDomainAndSlug((component as GroupedComponentsWrapper).components);
        if (result.domain && result.slug) {
          domain = result.domain;
          slug = result.slug;
        }
      }

      if (domain && slug) {
        break;
      }
    }
  }

  return { domain, slug };
};

/**
 * @description This provider is used to fetch the domain and slug for an embedded promo template page.
 */
const EmbeddedPromoTemplatePageProvider = (props: React.PropsWithChildren<{}>) => {
  const componentsConfig = useComponentsConfig();
  const { domain, slug } = extractDomainAndSlug(componentsConfig);

  return (
    <PromoTemplateProvider>
      <PromoTemplateDomainProvider domain={domain as keyof typeof DOMAINS}>
        <NativeDataProvider>
          <EmbeddedPromoTemplatePageProviderWrapper slug={slug} domain={domain}>
            {props.children}
          </EmbeddedPromoTemplatePageProviderWrapper>
        </NativeDataProvider>
      </PromoTemplateDomainProvider>
    </PromoTemplateProvider>
  );
};

export default EmbeddedPromoTemplatePageProvider;
